/**
 * Waveform-driven camera motion for SongAI clips.
 * Audio/video playback stays 1× — only transform parameters follow the music.
 */

import { computeMotionTransform } from '@/lib/videoMotionPresets';
import {
  beatAnimateEveryFromSongSpeed,
  beatSyncIntensityFromSongSpeed,
  clampSongAnimationSpeed,
} from '@/lib/songAnimationSettings';

function lerp(a, b, t) {
  return a + (b - a) * Math.max(0, Math.min(1, t));
}

function smoothstep01(t) {
  const x = Math.max(0, Math.min(1, t));
  return x * x * (3 - 2 * x);
}

function sampleBeatPunchFactor(beatTimesRel, tSec, animateEvery, window = 0.14) {
  if (!beatTimesRel?.length || animateEvery >= 999) return 0;
  const every = Math.max(1, Math.round(animateEvery));
  const sorted = [...beatTimesRel]
    .map(Number)
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b);
  let best = 0;
  for (let idx = 0; idx < sorted.length; idx += 1) {
    if (every > 1 && idx % every !== 0) continue;
    const dist = Math.abs(sorted[idx] - tSec);
    if (dist <= window) {
      const bump = 1 - dist / window;
      best = Math.max(best, bump * bump * bump);
    }
  }
  return best;
}

function applyLiveBeatPunch(params, timeline, tSec, animationSpeed) {
  const beatTimes = timeline?.beat_times_rel;
  if (!beatTimes?.length || !params) return params;

  const intensity = beatSyncIntensityFromSongSpeed(animationSpeed);
  const songAvg = Number(timeline.song_avg_amplitude) || 0.5;
  const songPeak = Number(timeline.song_peak_amplitude) || songAvg;
  const rawAmp = Number(params.raw_amplitude) || 0.5;
  const aboveAvg = rawAmp > songAvg;
  const ampExcess = Math.max(0, rawAmp - songAvg);
  const ampHeadroom = Math.max(0.08, songPeak - songAvg);

  const animateEvery = beatAnimateEveryFromSongSpeed(animationSpeed);
  const beatRaw = sampleBeatPunchFactor(beatTimes, tSec, animateEvery);
  const livePunch = aboveAvg ? beatRaw * smoothstep01(ampExcess / ampHeadroom) : 0;

  const storedPunch = Number(params.beat_punch) || 0;
  const beatZoom = 0.028 * intensity;
  const beatRot = 0.28 * intensity;
  const baseScale = (Number(params.scale) || 1) - storedPunch * (0.028 * Math.max(1, intensity * 0.5));
  const baseRotate = (Number(params.rotate_deg) || 0) - storedPunch * (0.28 * Math.max(1, intensity * 0.5));
  const punchRatio = storedPunch > 0.001
    ? livePunch / storedPunch
    : (livePunch > 0 ? 1 : 0);

  return {
    ...params,
    beat_punch: livePunch,
    scale: Math.max(1, baseScale + livePunch * beatZoom),
    rotate_deg: baseRotate + livePunch * beatRot,
    shake_px: storedPunch > 0.001
      ? (Number(params.shake_px) || 0) * Math.max(0, Math.min(2, punchRatio)) * intensity
      : (livePunch > 0.2
        ? (Number(params.shake_px) || 0) * intensity
        : (Number(params.shake_px) || 0) * 0.45 * intensity),
    _beatSyncIntensity: intensity,
  };
}

function applyBeatSyncIntensity(params, animationSpeed) {
  if (!params) return params;
  const intensity = params._beatSyncIntensity ?? beatSyncIntensityFromSongSpeed(animationSpeed);
  if (intensity <= 0.001) return params;
  const scale = Number(params.scale) || 1;
  return {
    ...params,
    pan_x: (Number(params.pan_x) || 0) * intensity,
    pan_y: (Number(params.pan_y) || 0) * intensity,
    scale: 1 + (scale - 1) * intensity,
    rotate_deg: (Number(params.rotate_deg) || 0) * intensity,
    shake_px: (Number(params.shake_px) || 0) * intensity,
    _beatSyncIntensity: intensity,
  };
}

export function sampleAudioMotionTimeline(timeline, localSec, animationSpeed = 1) {
  if (!timeline || typeof timeline !== 'object') return null;
  const keyframes = Array.isArray(timeline.keyframes) ? timeline.keyframes : [];
  if (!keyframes.length) return null;

  const sampleHz = Number(timeline.sample_hz) || 20;
  const duration = Number(timeline.duration_seconds) || 0;
  const t = Math.max(0, Math.min(duration || 1e9, Number(localSec) || 0));
  const idx = t * sampleHz;
  const i0 = Math.max(0, Math.min(keyframes.length - 1, Math.floor(idx)));
  const i1 = Math.min(keyframes.length - 1, i0 + 1);
  const frac = idx - i0;
  const a = keyframes[i0] || {};
  const b = keyframes[i1] || {};

  const mix = (key, fallback = 0) => lerp(Number(a[key] ?? fallback), Number(b[key] ?? fallback), frac);

  const params = {
    motion_intensity: mix('motion_intensity', 0.5),
    energy: mix('energy', 0.5),
    raw_amplitude: mix('raw_amplitude', 0.5),
    scale: mix('scale', 1),
    pan_x: mix('pan_x'),
    pan_y: mix('pan_y'),
    rotate_deg: mix('rotate_deg'),
    shake_px: mix('shake_px'),
    beat_punch: mix('beat_punch'),
    base_preset: timeline.base_preset || 'ken_burns',
    base_strength: Number(timeline.base_strength) || 0.12,
  };

  return applyBeatSyncIntensity(
    applyLiveBeatPunch(params, timeline, t, animationSpeed),
    animationSpeed,
  );
}

export function clipHasAudioMotion(clip) {
  return Boolean(clip?.audioMotionTimeline?.keyframes?.length);
}

export function clipLocalPlaybackTime(clip, timelineTime) {
  return Math.max(0, Number(timelineTime) - (Number(clip?.startTime) || 0));
}

/**
 * CSS transform driven by audio energy at the current timeline position.
 * @param {number} [animationSpeed=1] — beat-sync slider: every beat (100%) … every 10th beat (slow).
 */
export function audioMotionPreviewStyle(timeline, localSec, animationSpeed = 1) {
  const params = sampleAudioMotionTimeline(timeline, localSec, animationSpeed);
  if (!params) return {};

  const speed = clampSongAnimationSpeed(animationSpeed);
  if (speed <= 0) return {};
  const intensity = params._beatSyncIntensity ?? beatSyncIntensityFromSongSpeed(animationSpeed);
  const duration = Math.max(0.05, Number(timeline.duration_seconds) || 1);
  const progress = Math.max(0, Math.min(1, Number(localSec) / duration));
  const drive = Number(params.motion_intensity) || 0.2;
  const driveStrength = (Number(params.base_strength) || 0.1) * (0.4 + drive * 0.55) * (0.75 + intensity * 0.35);

  const presetT = computeMotionTransform(
    params.base_preset || 'slow_drift',
    progress,
    driveStrength,
  );

  const floatSoft = speed < 0.05
    ? Math.max(0, Math.min(1, (0.05 - speed) / 0.0495))
    : 0;
  const calm = 1 - floatSoft * 0.94;
  const presetBlend = 0.28 * (0.12 + intensity * 0.45);

  const scale = Math.max(params.scale, presetT.scale * 0.45 + params.scale * 0.55);
  const tx = params.pan_x + presetT.tx * presetBlend;
  const ty = params.pan_y + presetT.ty * presetBlend;
  const rotate = params.rotate_deg + presetT.rotate * (0.28 * calm);
  const shakeScale = Math.max(0, Math.min(1, (drive - 0.25) / 0.35));
  const shake = (params.shake_px || 0) * shakeScale * calm * (0.55 + intensity * 0.35);
  const shakeHz = (4 + drive * 8) * Math.max(0.15, intensity * 1.2);
  const shakeX = shake > 0.5 ? (Math.sin(localSec * shakeHz) * shake) / 140 : 0;
  const shakeY = shake > 0.5 ? (Math.cos(localSec * shakeHz * 0.9) * shake) / 140 : 0;

  return {
    transform: `translate3d(${(tx + shakeX) * 100}%, ${(ty + shakeY) * 100}%, 0) scale(${scale}) rotate(${rotate}deg)`,
    transformOrigin: 'center',
    willChange: 'transform',
  };
}

export function resolveClipMotionPreviewStyle(clip, timelineTime, opts = {}) {
  const {
    defaultAnimationEnabled = true,
    animationSpeed = 1,
  } = opts;

  if (!defaultAnimationEnabled) {
    return null;
  }

  if (clipHasAudioMotion(clip)) {
    let local = clipLocalPlaybackTime(clip, timelineTime);
    if (
      clip.parentSceneId != null
      && clip.multishotPart != null
      && Number(clip.multishotSegmentDuration) > 0
    ) {
      local = Number(clip.multishotPart) * Number(clip.multishotSegmentDuration) + local;
    }
    const speed = clampSongAnimationSpeed(animationSpeed);
    if (speed <= 0) return null;
    return audioMotionPreviewStyle(clip.audioMotionTimeline, local, speed);
  }
  return null;
}

export function segmentToClipMotionFields(segment) {
  if (!segment) return {};
  const timeline = segment.audio_motion_timeline || segment.audioMotionTimeline;
  const meta = segment.beat_sync_metadata || {};
  return {
    motionPreset: segment.motion_preset || meta.motion_preset || 'slow_drift',
    motionStrength: Number(segment.motion_strength ?? meta.motion_strength) || 0.1,
    audioMotionTimeline: timeline && timeline.keyframes?.length ? timeline : null,
    beatSyncMetadata: meta,
  };
}
